/**
 * The product mark.
 *
 * Two files, one component: the mark is drawn in the brand navy, which is
 * invisible on the dark theme's near-black, so there is a second drawing with
 * those strokes in white. Picking between them is this component's whole job —
 * which is why nothing else may import the SVGs directly.
 */
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import markOnLight from '../assets/fortes-lock.svg';
import markOnDark from '../assets/fortes-lock-on-dark.svg';

export interface LogoProps {
  /** Height of the mark in pixels. The mark is square. */
  size?: number;
  /** Renders the wordmark beside it. Off for a bare icon. */
  withWordmark?: boolean;
  /** Size of the wordmark. Ignored without `withWordmark`. */
  wordmarkVariant?: 'h5' | 'h6' | 'subtitle1';
  /** A second line under the wordmark. Ignored without `withWordmark`. */
  subtitle?: string;
}

export function Logo({
  size = 32,
  withWordmark = false,
  wordmarkVariant = 'h6',
  subtitle,
}: LogoProps) {
  const theme = useTheme();
  const mark = theme.palette.mode === 'dark' ? markOnDark : markOnLight;

  return (
    <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
      <Box
        component="img"
        src={mark}
        alt="FORSYS"
        sx={{ width: size, height: size, display: 'block', flexShrink: 0 }}
      />
      {withWordmark && (
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant={wordmarkVariant}
            component="span"
            sx={{ display: 'block', fontWeight: 700, letterSpacing: '0.04em', lineHeight: 1.15 }}
          >
            FORSYS
          </Typography>
          {subtitle && (
            <Typography
              variant="caption"
              component="span"
              color="text.secondary"
              sx={{ display: 'block', lineHeight: 1.3 }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      )}
    </Stack>
  );
}
